import { map } from "rxjs/operators";
import { Injectable, inject } from "@angular/core";

import { Observable } from "rxjs";

import { QuoteEmailMapper } from "./models/email.mapper";
import { QuoteLocationMapper } from "./models/quote-location.mapper";
import { QuoteLocation } from "./models/quote-location.model";
import { QuoteTerms } from "./quote-terms/quote-terms.model";
import { environment } from "environments/environment";
import { Quote } from "./quote.type";
import { LocationMapper } from "../location/location.mapper";
import { Location } from "../location/location.model";
import { HttpClient } from "@angular/common/http";
import { PaginationList } from "../shared/grid/pagination-list.model";
import { QuoteMapper } from "./models/quote.mapper";
import { QuoteEmail } from "./models/email.model";
import { HttpParams } from "@angular/common/http";
import { PaginatedResponse } from "../shared/http/paginated-response.model";
import { Rate } from "../rate/rate-type";
import { SurchargeType } from "../surcharge-type/surcharge-type.model";
import { QuoteSurchargeTypeMapper } from "./models/quote-surcharge-type.mapper";
import { QuoteSurchargeType } from "./models/quote-surcharge-type.model";
import { quoteType } from "./quote-form/quote-type.const";
import { BaseQuote } from "./models/quote-base.model";
import { QuoteExchangeRates } from "./models/quote-exchange-rates.model";

@Injectable()
export class QuoteRepository {
  private http = inject(HttpClient);

  public getQuoteList(
    params: HttpParams = new HttpParams(),
  ): Observable<PaginationList<Quote>> {
    return this.http
      .get<PaginatedResponse>(`${environment.apiUrl}/quote`, { params: params })
      .pipe(
        map(
          (list) =>
            new PaginationList<Quote>(
              list.items.map(QuoteMapper.map),
              list.total,
            ),
        ),
      );
  }

  public getChildQuotes(quoteId): Observable<Quote[]> {
    return this.http
      .get<any>(`${environment.apiUrl}/multiquote/${quoteId}/quotes`)
      .pipe(map((list) => list.map(QuoteMapper.map)));
  }

  public getQuote(quoteId): Observable<Quote> {
    return this.http
      .get(`${environment.apiUrl}/quote/${quoteId}`)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public refreshAverageMargin(quoteId): Observable<Quote> {
    return this.http
      .post(`${environment.apiUrl}/quote/${quoteId}/refresh`, null)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public getMailMessages(quoteId): Observable<QuoteEmail[]> {
    return this.http
      .get<QuoteEmail[]>(`${environment.apiUrl}/quote/${quoteId}/mailSubjects`)
      .pipe(map((response) => response.map(QuoteEmailMapper.map)));
  }

  public getEmailPlaceholder(quoteId): Observable<QuoteEmail> {
    return this.http
      .get(`${environment.apiUrl}/quote/${quoteId}/email/placeholder`)
      .pipe(map((response) => QuoteEmailMapper.map(response)));
  }

  public createQuote(payload?: any, type = quoteType.fcl): Observable<Quote> {
    type = type ? type : quoteType.fcl;
    return this.http
      .post(`${environment.apiUrl}/quote/${type}`, payload)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public replicateQuote(quoteId: number, keepTotalPrice): Observable<Quote> {
    return this.http
      .patch(
        `${environment.apiUrl}/quote/${quoteId}/replicate?keepTotalPrice=${keepTotalPrice}`,
        { keepTotalPrice },
      )
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public ai(
    quoteId: number,
    text: string,
  ): Observable<{ confidence: number | null }> {
    return this.http
      .patch<{
        confidence: number | null;
      }>(`${environment.apiUrl}/quote/${quoteId}/ai`, { text: text })
      .pipe(map((response) => response));
  }

  public negotiateQuote(quoteId: number): Observable<Quote> {
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}/negotiate`, null)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public getPotential(quoteId: number): Observable<boolean> {
    return this.http
      .get<any>(`${environment.apiUrl}/quote/${quoteId}/potential`)
      .pipe(map((response) => (response.potentialExists ? true : false)));
  }

  public acceptQuote(quoteId: number): Observable<Quote> {
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}/accept`, null)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public unacceptQuote(quoteId: number): Observable<Quote> {
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}/unaccept`, null)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public declineQuote(
    quoteId: number,
    reasonOfDecline: string = null,
  ): Observable<Quote> {
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}/decline`, {
        reasonOfDecline: reasonOfDecline,
      })
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public undeclineQuote(quoteId: number): Observable<Quote> {
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}/undecline`, null)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public deleteQuote(quoteId: number): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/quote/${quoteId}`);
  }

  public patchQuote(quoteId: number, payload: any): Observable<Quote> {
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}`, payload)
      .pipe(map((response) => QuoteMapper.map(response)));
  }

  public sendQuote(
    quoteId: number,
    replyToMessageId?: string,
    replyToReceivedAt?: string,
  ): Observable<object> {
    const body: Record<string, string> = {};
    if (replyToMessageId) {
      body["replyToMessageId"] = replyToMessageId;
    }
    if (replyToReceivedAt) {
      body["replyToReceivedAt"] = replyToReceivedAt;
    }
    return this.http
      .patch(`${environment.apiUrl}/quote/${quoteId}/send`, body)
      .pipe(map((response) => response));
  }

  // TODO: We should operate on QuoteLocation model here
  public addOrigin(
    quoteId: number,
    locationId: number,
    inlandHaulage: string,
    postalCode: string,
    latitude: number,
    longitude: number,
    country: string = null,
    locationGroupId: number = undefined,
  ): Observable<QuoteLocation> {
    return this.http
      .post(`${environment.apiUrl}/quote/${quoteId}/origin`, {
        location: locationId,
        inlandHaulage: inlandHaulage,
        postalCode: postalCode,
        latitude: latitude,
        longitude: longitude,
        country: country,
        locationGroup: locationGroupId,
      })
      .pipe(map((response) => QuoteLocationMapper.map(response)));
  }

  // TODO: We should operate on QuoteLocation model here
  public patchOrigin(
    quoteId: number,
    quoteLocationId: number,
    location: Location,
    inland: string,
    postalCode: string,
    latitude: number,
    longitude: number,
    country: string = null,
    locationGroupId: number = undefined,
  ): Observable<QuoteLocation> {
    return this.http
      .patch(`${environment.apiUrl}/origin/${quoteLocationId}`, {
        location: location ? location.getId() : undefined,
        inlandHaulage: inland,
        postalCode: postalCode,
        latitude: latitude,
        longitude: longitude,
        country: country,
        locationGroup: locationGroupId,
      })
      .pipe(map((response) => QuoteLocationMapper.map(response)));
  }

  public deleteOrigin(quoteId: number, id: number): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/origin/${id}`);
  }

  public addDestination(
    quoteId: number,
    locationId: number,
    inlandHaulage: string,
    postalCode: string,
    latitude: number,
    longitude: number,
    country: string = null,
    locationGroupId: number = undefined,
  ): Observable<QuoteLocation> {
    return this.http
      .post(`${environment.apiUrl}/quote/${quoteId}/destination`, {
        location: locationId,
        inlandHaulage: inlandHaulage,
        postalCode: postalCode,
        latitude: latitude,
        longitude: longitude,
        country: country,
        locationGroup: locationGroupId,
      })
      .pipe(map((response) => QuoteLocationMapper.map(response)));
  }

  public patchDestination(
    quoteId: number,
    quoteLocationId: number,
    location: Location,
    inland: string,
    postalCode: string,
    latitude: number,
    longitude: number,
    country: string = null,
    locationGroupId: number = undefined,
  ): Observable<QuoteLocation> {
    return this.http
      .patch(`${environment.apiUrl}/destination/${quoteLocationId}`, {
        location: location ? location.getId() : undefined,
        inlandHaulage: inland,
        postalCode: postalCode,
        latitude: latitude,
        longitude: longitude,
        country: country,
        locationGroup: locationGroupId,
      })
      .pipe(map((response) => QuoteLocationMapper.map(response)));
  }

  public deleteDestination(quoteId: number, id: number): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/destination/${id}`);
  }

  public getPreview(quoteId: number): Observable<{ url: string }> {
    // TODO: Create model for this?
    return this.http
      .get<{ url: string }>(`${environment.apiUrl}/quote/${quoteId}/preview`)
      .pipe(map((response) => response));
  }

  public getEmailPreview(quoteId: number): Observable<QuoteEmail> {
    return this.http
      .get(`${environment.apiUrl}/quote/${quoteId}/email/preview`)
      .pipe(map((response) => QuoteEmailMapper.map(response)));
  }

  public getEmailPlaceholders(
    quoteId: number,
  ): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(
      `${environment.apiUrl}/quote/${quoteId}/email/placeholders`,
    );
  }

  public getTerms(quoteId: number): Observable<QuoteTerms> {
    // TODO: Add mapper for QuoteTerms
    return this.http.get<QuoteTerms>(
      `${environment.apiUrl}/quote/${quoteId}/terms`,
    );
  }

  public getExchangeRates(
    quoteId: number,
  ): Observable<QuoteExchangeRates | null> {
    return this.http
      .get<QuoteExchangeRates | null>(
        `${environment.apiUrl}/quote/${quoteId}/exchangeRates`,
      )
      .pipe(map((response) => (response?.rates?.length ? response : null)));
  }

  public getFclTariffContractLocations(quoteId: number): Observable<{
    originLocations: Location[];
    destinationLocations: Location[];
  }> {
    // TODO: Create model for this
    return this.http
      .get<any>(`${environment.apiUrl}/quote/${quoteId}/rateLocations`)
      .pipe(
        map((response) => {
          return {
            originLocations: response.originLocations
              ? response.originLocations.map(LocationMapper.map)
              : [],
            destinationLocations: response.destinationLocations
              ? response.destinationLocations.map(LocationMapper.map)
              : [],
          };
        }),
      );
  }

  public createQuoteSurchargeType(
    quote: BaseQuote,
    rate: Rate,
    surchargeType: SurchargeType,
  ): Observable<QuoteSurchargeType> {
    return this.http
      .post(
        `${
          environment.apiUrl
        }/quote/${quote.getId()}/rate/${rate.getId()}/surchargeType/${surchargeType.getId()}`,
        {},
      )
      .pipe(map((response) => QuoteSurchargeTypeMapper.map(response)));
  }

  public deleteQuoteSurchargeType(
    surcharge: QuoteSurchargeType,
  ): Observable<object> {
    return this.http.delete(
      `${environment.apiUrl}/quoteratesurcharge/${surcharge.getId()}`,
    );
  }

  public getDebugData(quoteId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/quote/${quoteId}/debug`);
  }

  public downloadFile(quote: BaseQuote): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}/quote/${quote.getId()}/pdf`, {
        responseType: "blob",
      })
      .pipe(map((res) => new Blob([res], { type: "application/pdf" })));
  }

  public downloadFileInternal(quote: BaseQuote): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}/quote/${quote.getId()}/pdfInternal`, {
        responseType: "blob",
      })
      .pipe(map((res) => new Blob([res], { type: "application/pdf" })));
  }
}
